import { useEffect, useRef, useCallback } from "react";
import toast from "react-hot-toast";
import { MessageRepository } from "@amityco/ts-sdk";

/**
 * useChatNotification
 *
 * Watches all channels for new messages from OTHER users and fires:
 *   1. A browser Notification (works even when tab is not focused)
 *   2. A react-hot-toast banner (visible when tab IS focused)
 *
 * Usage:
 *   const { requestPermission } = useChatNotification({
 *     channels,          // array of chat list items from the API
 *     currentCreatorId,  // Amity userId of the logged-in client, e.g. "A42"
 *     activeChannelId,   // channel_code the user currently has open (no notif for this)
 *   });
 *
 * Call requestPermission() once after the user logs in (needs a user gesture).
 */
export function useChatNotification({
  channels,
  currentCreatorId,
  activeChannelId,
}) {
  const seenSegmentsRef = useRef({}); // { [channelKey]: lastSeenSegment }
  const disposersRef = useRef([]);

  // Request browser notification permission (must be called from a user gesture)
  const requestPermission = useCallback(async () => {
    if ("Notification" in window && Notification.permission === "default") {
      await Notification.requestPermission();
    }
  }, []);

  // Show a browser notification + toast
  const showNotification = useCallback(
    (senderName, text, channelCode) => {
      // Toast (visible when tab is focused)
      toast.custom(
        (t) => (
          <div
            onClick={() => toast.dismiss(t.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
              padding: "12px 16px",
              cursor: "pointer",
              maxWidth: "340px",
              borderLeft: "4px solid #4f7cf7",
              animation: t.visible ? "slideIn 0.3s ease" : "slideOut 0.2s ease",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #4f7cf7, #7f53f7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: "bold",
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {senderName?.[0]?.toUpperCase() || "?"}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
                {senderName}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#555",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 240,
                }}
              >
                {text}
              </div>
            </div>
          </div>
        ),
        { duration: 5000 }
      );

      // Browser / OS notification (works when tab is in background)
      if ("Notification" in window && Notification.permission === "granted") {
        const n = new Notification(senderName || "New message", {
          body: text,
          icon: "/favicon.ico",
          tag: channelCode, // replace previous notif for same channel
        });
        n.onclick = () => {
          window.focus();
          n.close();
        };
      }
    },
    []
  );

  useEffect(() => {
    // Clean up previous listeners
    disposersRef.current.forEach((fn) => fn && fn());
    disposersRef.current = [];

    if (!channels?.length || !currentCreatorId) return;

    channels.forEach((item) => {
      const channelKey = item.channel_code;
      const scid = item.sub_channel_id || item.channel_code;
      if (!scid) return;

      const unsub = MessageRepository.getMessages(
        { subChannelId: scid, limit: 1, isDeleted: false },
        ({ data }) => {
          if (!data?.length) return;

          const latest = data[0];
          const segment = latest?.channelSegment ?? 0;

          // First load — just record the segment, don't fire a notification
          if (seenSegmentsRef.current[channelKey] === undefined) {
            seenSegmentsRef.current[channelKey] = segment;
            return;
          }

          // New message has arrived
          if (
            segment > seenSegmentsRef.current[channelKey] &&
            latest.creatorId !== currentCreatorId &&
            channelKey !== activeChannelId // already viewing this chat
          ) {
            seenSegmentsRef.current[channelKey] = segment;
            const senderName =
              latest.metadata?.data?.sender ||
              item.provider_array?.[0] ||
              "New message";
            const text = latest.data?.text || "Sent you a message";
            showNotification(senderName, text, channelKey);
          } else {
            seenSegmentsRef.current[channelKey] = segment;
          }
        }
      );

      disposersRef.current.push(unsub);
    });

    return () => {
      disposersRef.current.forEach((fn) => fn && fn());
      disposersRef.current = [];
    };
  }, [channels, currentCreatorId, activeChannelId, showNotification]);

  return { requestPermission };
}
